<!--
  Resting placeholder shown until @algolia/autocomplete-js is lazy-initialized.
  It mirrors the autocomplete's own resting DOM (.aa-* classes) so the shared,
  global federated-search.css styles it identically — no visual shift on first
  focus. Renders the inline form on >=680px and the detached search button below
  it, matching autocomplete-js's own `--aa-detached-media-query: (max-width: 680px)`.

  The root wrapper receives the `fullwidth` class from the parent so the
  `.fullwidth .aa-Autocomplete` ancestor selector resolves the same way it does
  around the mounted autocomplete.
-->
<template>
  <div class="aa-fsp">
    <div
      class="aa-Autocomplete"
      role="combobox"
      aria-expanded="false"
      aria-haspopup="listbox"
    >
      <!-- Inline form (desktop, >=680px) -->
      <div class="aa-fsp-inline">
        <form class="aa-Form" novalidate @submit.prevent>
          <div class="aa-InputWrapperPrefix">
            <label class="aa-Label">
              <button
                class="aa-SubmitButton"
                type="button"
                tabindex="-1"
                aria-hidden="true"
              >
                <svg
                  class="aa-SubmitIcon"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="currentColor"
                >
                  <path :d="searchIconPath"></path>
                </svg>
              </button>
            </label>
          </div>
          <div class="aa-InputWrapper">
            <input
              class="aa-Input"
              :placeholder="placeholderText"
              :aria-label="placeholderText"
              :data-test-id="withTestIds ? 'federated-search-input' : undefined"
            />
          </div>
        </form>
      </div>

      <!-- Detached search button (mobile, <680px) -->
      <div class="aa-fsp-detached">
        <button
          type="button"
          class="aa-DetachedSearchButton"
          :title="placeholderText"
          :data-test-id="
            withTestIds ? 'federated-search-detached-button' : undefined
          "
        >
          <div class="aa-DetachedSearchButtonIcon">
            <svg
              class="aa-SubmitIcon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
            >
              <path :d="searchIconPath"></path>
            </svg>
          </div>
          <div class="aa-DetachedSearchButtonPlaceholder">
            {{ placeholderText }}
          </div>
          <div class="aa-DetachedSearchButtonQuery"></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

// Set only by the live <FederatedSearch> placeholder, so the CLS-only spacer
// copies don't duplicate the e2e hooks.
const { withTestIds = false } = defineProps<{ withTestIds?: boolean }>()

const { t } = useI18n()
const placeholderText = t('search.algoliaAutocomplete.placeholder')

// @algolia/autocomplete-js' default submit-icon glyph (mirrored here so the
// resting state matches the mounted autocomplete pixel-for-pixel).
const searchIconPath =
  'M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z'
</script>

<style scoped lang="css">
/* Mirror autocomplete-js' detached breakpoint: inline >=680px, detached below. */
.aa-fsp-detached {
  display: none;
}

@media (max-width: 680px) {
  .aa-fsp-inline {
    display: none;
  }

  .aa-fsp-detached {
    display: block;
  }
}
</style>
